import React, { useEffect, useState } from 'react';
import { Box, Button, Link as MuiLink, Typography } from '@mui/material';
import { apiClient } from '../../apiClient';
import { ShipmentScan, ShipmentTimeline } from '../../types';

interface ShipmentTimelineCardProps {
  orderId: string;
  waybill: string;
  /** Fallback single-line status shown while the timeline loads or if it fails */
  lastScan?: string;
}

const INITIAL_VISIBLE = 4;

const formatScanTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const ShipmentTimelineCard: React.FC<ShipmentTimelineCardProps> = ({
  orderId,
  waybill,
  lastScan
}) => {
  const [timeline, setTimeline] = useState<ShipmentTimeline | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let active = true;
    apiClient
      .get(`/shipping/orders/${orderId}/tracking`)
      .then((res) => {
        if (active) setTimeline(res.data as ShipmentTimeline);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [orderId]);

  // Newest first for display
  const scans: ShipmentScan[] = timeline ? [...timeline.scans].reverse() : [];
  const visibleScans = expanded ? scans : scans.slice(0, INITIAL_VISIBLE);
  const delivered = Boolean(timeline?.deliveredAt);

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.08)',
        bgcolor: delivered ? 'rgba(46,125,50,0.05)' : 'rgba(112, 97, 218, 0.04)'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
          flexWrap: 'wrap'
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
            📦 Shipment tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Delhivery · Tracking ID {waybill}
          </Typography>
        </Box>
        <MuiLink
          href={`https://www.delhivery.com/track-v2/package/${waybill}`}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          variant="body2"
        >
          Track on Delhivery →
        </MuiLink>
      </Box>

      {scans.length === 0 && lastScan && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {lastScan}
        </Typography>
      )}

      {scans.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {visibleScans.map((scan, index) => {
            const isLatest = index === 0;
            const isLast = index === visibleScans.length - 1;
            return (
              <Box key={`${scan.at}-${index}`} sx={{ display: 'flex', gap: 1.5 }}>
                {/* Dot + connector */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      mt: '5px',
                      flexShrink: 0,
                      bgcolor: isLatest
                        ? delivered
                          ? 'success.main'
                          : 'primary.main'
                        : 'rgba(43,37,43,0.25)'
                    }}
                  />
                  {!isLast && (
                    <Box sx={{ width: 2, flex: 1, bgcolor: 'rgba(43,37,43,0.12)', my: '2px' }} />
                  )}
                </Box>
                <Box sx={{ pb: isLast ? 0 : 1.5, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: isLatest ? 600 : 400 }}
                    color={isLatest ? 'text.primary' : 'text.secondary'}
                  >
                    {scan.status}
                  </Typography>
                  {scan.remarks && scan.remarks !== scan.status && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {scan.remarks}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {[scan.location, formatScanTime(scan.at)].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Box>
            );
          })}
          {scans.length > INITIAL_VISIBLE && (
            <Button size="small" variant="text" onClick={() => setExpanded(!expanded)} sx={{ mt: 0.5, px: 0 }}>
              {expanded ? 'Show less' : `Show all ${scans.length} updates`}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ShipmentTimelineCard;
